// frontend/src/pages/CaptionGeneratorPage.js
//
// Renders the four caption/post generator pages from
// data/caption-generator-pages.json.
//
// WHICH PAGES EXIST, AND WHY — all measured 2026-08-16:
//
//   instagram-caption-generator   18,000/mo across five phrasings, KD 10-17
//   youtube-description-generator  2,600/mo — `youtube title generator` 1,600
//                                  at KD 10 + `youtube description generator`
//                                  1,000 at KD 8. Both on one page: they are
//                                  the same task and the tool returns both.
//   facebook-post-generator          880/mo at KD 4 — the lowest difficulty
//                                  measured anywhere this week
//   tiktok-caption-generator         590/mo at KD 0
//
// DELIBERATELY NOT BUILT, each for a reason:
//   `tweet generator`      2,900/mo, KD 21 — but the SERP is tweetgen.com,
//                          tweethunter.io/fake-tweet-generator and zeoob.com.
//                          These are FAKE TWEET IMAGE generators, a novelty
//                          product. Only one organic result is a real writer.
//   `snapchat caption generator`  1,000/mo at KD 0, the cheapest volume in the
//                          family — but we do not publish to Snapchat, so the
//                          tool would work and the CTA would be false.
//   `linkedin post generator`  480/mo but KD 39; `linkedin caption generator`
//                          only 40/mo. LinkedIn is the weakest network here.
//   `pinterest description generator` 40/mo, `x caption generator` 10/mo.
//
// INTENT TRAP AVOIDED: `video caption generator`, `auto caption generator` and
// `close caption generator` (880/mo each) are SUBTITLE tools, not social
// captions. Different product entirely. `meme caption generator` (3,600, KD 53)
// likewise.
//
// INTENT TRAP AVOIDED: `video caption generator`, `auto caption generator` and
// `close caption generator` (880/mo each) are SUBTITLE tools, not social
// captions. Different product entirely. `meme caption generator` (3,600, KD 53)
// likewise.
//
// useLocation, NOT useParams — static routes, no :param.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import CaptionGenerator from '../components/CaptionGenerator'
import pages from '../data/caption-generator-pages.json'
import NotFound from './NotFound'

const HERO = {
  'instagram-caption-generator': ['/assets/images/articles/caption-instagram.webp', 'Someone writing a post on a phone at a table'],
  'tiktok-caption-generator':    ['/assets/images/articles/caption-tiktok.webp',    'A creator reviewing a video on a phone'],
  'youtube-description-generator':['/assets/images/articles/caption-youtube.webp',  'A video editing setup on a desk'],
  'facebook-post-generator':     ['/assets/images/articles/caption-facebook.webp', 'Someone writing at a laptop'],
}

export default function CaptionGeneratorPage () {
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+tools\/+/, '').replace(/\/+$/, '')
  const p = pages.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const url = `/tools/${p.slug}`
  const [heroSrc, heroAlt] = HERO[p.slug] || HERO['instagram-caption-generator']

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={url}
        image={heroSrc}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: p.faqs.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: p.h1,
            url: `${SITE_URL}${url}`,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
          {
            '@context': 'https://schema.org', '@type': 'HowTo',
            name: p.howto_heading,
            step: p.howto.map(([name, text], i) => ({
              '@type': 'HowToStep', position: i + 1, name, text,
            })),
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-0'>{p.sub}</p>
            </Col>
            <Col lg={5}>
              <Image src={heroSrc} alt={heroAlt} fluid rounded loading='eager'
                     width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <CaptionGenerator platform={p.platform} />
        </Container>
      </section>

      {/* ───────── THE ANSWER (snippet target) ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <p className='fs-5' dangerouslySetInnerHTML={{ __html: p.intro }} />
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── HOW TO ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 780 }}>
          <h2 className='fw-bold mb-4'>{p.howto_heading}</h2>
          {p.howto.map(([heading, body], i) => (
            <div className='d-flex gap-3 mb-4' key={heading}>
              <div className='fw-bold text-primary flex-shrink-0'
                   style={{ fontSize: '1.5rem', lineHeight: 1.2, width: 32 }}>{i + 1}</div>
              <div>
                <div className='fw-semibold mb-1'>{heading}</div>
                <p className='text-muted mb-0'>{body}</p>
              </div>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WORKED EXAMPLES ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-2'>{p.examples_heading}</h2>
          <p className='text-muted mb-4'>{p.examples_intro}</p>
          <Row className='g-3'>
            {p.examples.map(([label, text], i) => {
              // On the Instagram page, show the cut-off inline — the same thing the
              // tool does, so the point lands before anyone runs a generation.
              const chars = [...text]
              const cut = p.visible && chars.length > p.visible
              const shown = cut ? chars.slice(0, p.visible).join('') : text
              const hidden = cut ? chars.slice(p.visible).join('') : ''
              return (
                <Col md={6} key={label}>
                  <div className='p-3 rounded-3 h-100' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
                    <div className={`small text-uppercase mb-2 fw-semibold ${i === 0 ? 'text-danger' : 'text-success'}`}
                         style={{ letterSpacing: '.04em' }}>
                      {label}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      <span>{shown}</span>
                      {hidden && (
                        <>
                          <span className='text-muted' style={{ opacity: 0.42 }}>{hidden}</span>
                          <span className='text-primary small fw-semibold'> … more</span>
                        </>
                      )}
                    </div>
                    <div className='small text-muted mt-2' style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {chars.length} characters{p.visible ? ` · ${p.visible} visible` : ''}
                    </div>
                  </div>
                </Col>
              )
            })}
          </Row>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {p.sections.map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can write a post once, keep a different caption for each network,
                and schedule it to all eight — Instagram, TikTok, Facebook, LinkedIn, YouTube,
                Pinterest, Threads and X — from one calendar.
              </p>
              <p className='text-muted'>
                Which is exactly what the tool above just did. In the product those versions
                stay attached to one post instead of living in your clipboard. Plans start at
                $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/aipost.webp' alt='Writing a post with AI in Greatly Social'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {pages.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug}>
                <a href={`/tools/${x.slug}`} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.platformLabel} caption generator</div>
                  <div className='text-muted small'>Every network at once</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/tools/instagram-bio-generator' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Bio generator</div>
                <div className='text-muted small'>Fits the character limit</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/tools/engagement-rate-calculator' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Engagement rate calculator</div>
                <div className='text-muted small'>Per-platform formulas</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/best-time-to-post' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Best time to post</div>
                <div className='text-muted small'>A schedule for your audience</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>Writing it is the easy part</h2>
          <p className='text-muted fs-5'>
            At Greatly Social you can schedule a week across eight networks in one sitting,
            each with its own caption.
          </p>
          <Row className='g-3 mb-4'>
            {[['A caption per network', 'One post, six versions, no clipboard.'],
              ['Schedule once, publish weekly', 'Set your slots and let them run.'],
              ['A month in one upload', 'Queue a whole month from a spreadsheet.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a list.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can write once and post everywhere</h2>
          <p className='lead mb-4'>
            A different caption per network on the same post, scheduled in one pass. Free for
            14 days, no card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/ai-post-creation' className='mb-2'>
            See AI post creation
          </Button>
        </div>
      </section>
    </>
  )
}
